import { useState, useEffect } from 'react';
import { Check, ChevronDown, ChevronUp, X } from 'lucide-react';
import { Select } from 'antd';

const StatusFilter = ({ onFilterChange }) => {
    // Status groups with their respective items
    const statusGroups = {
        "Needs Approval": [
            "Application Under Review",
            "Profile Approval Requested",
            "Has Roles Pending Approval",
        ],
        "Needs Appeal Decision": [
            "Has Profile Appeal Pending Approval",
            "Has Roles Appeals Pending Approval",
        ],
        "Active": [
            "Applying",
            "Building Profile",
            "Active",
        ],
        "Other": [
            "Unregistered",
            "Invited by Client",
        ],
        "Rejected": [
            "Rejected Application",
            "Profile Rejected",
            "Deactivated",
            "Restricted",
            "Disabled",
            "Archived",
        ]
    };

    // State for selected statuses
    const [selectedStatuses, setSelectedStatuses] = useState([]);

    // State for dropdown open/closed
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    // Handle checkbox group selection
    const handleGroupSelection = (group, isChecked) => {
        const groupStatuses = statusGroups[group];

        if (isChecked) {
            // Add all statuses from the group
            const a = [...selectedStatuses, ...groupStatuses]
            setSelectedStatuses(a)
            onFilterChange(a);
            // setSelectedStatuses(prev => [...prev, ...groupStatuses]);
        } else {
            // Remove all statuses from the group
            const a = selectedStatuses.filter(status => !groupStatuses.includes(status));
            setSelectedStatuses(a)
            onFilterChange(a);
            // setSelectedStatuses(prev => prev.filter(status => !groupStatuses.includes(status)));
        }
    };

    // Handle individual status selection
    const handleStatusSelection = (status, isChecked) => {
        if (isChecked) {
            const a = [...selectedStatuses, status]
            setSelectedStatuses(a)
            onFilterChange(a);
            // setSelectedStatuses(prev => [...prev, status]);
        } else {
            const a = selectedStatuses.filter(item => item !== status);
            setSelectedStatuses(a)
            onFilterChange(a);
            // setSelectedStatuses(prev => prev.filter(item => item !== status));
        }
    };

    const handleStatusSelectionDropdown = (statuses) => {
        const result = [...new Set([...selectedStatuses, ...statuses])]
        console.log(result, 'result')
        setSelectedStatuses(result)
        onFilterChange(result);
    };

    // Check if a group is fully selected
    const isGroupSelected = (group) => {
        const groupStatuses = statusGroups[group];
        return groupStatuses.every(status => selectedStatuses.includes(status));
    };

    // Check if a group is partially selected
    const isGroupPartiallySelected = (group) => {
        const groupStatuses = statusGroups[group];
        const selectedGroupStatuses = groupStatuses.filter(status =>
            selectedStatuses.includes(status)
        );

        return selectedGroupStatuses.length > 0 &&
            selectedGroupStatuses.length < groupStatuses.length;
    };

    // Handle clearing all filters
    const clearAllFilters = () => {
        setSelectedStatuses([]);
        onFilterChange([]);
    };

    // Remove a specific filter
    const removeFilter = (status) => {
        const a = selectedStatuses.filter(item => item !== status);
        setSelectedStatuses(a)
        onFilterChange(a);
        // setSelectedStatuses(prev => prev.filter(item => item !== status));
    };

    // Update parent component when selection changes
    // useEffect(() => {
    //     if (onFilterChange) {
    //         onFilterChange(selectedStatuses);
    //     }
    // }, [selectedStatuses, onFilterChange]);

    return (
        <div className="w-full max-w3xl">
            <div className="mb-4">
                <h3 className="text-lg font-medium mb-3">Talent User Status Filter</h3>

                <div className="flex flex-wrap items-start gap-4">
                    {/* Primary checkbox groups in columns */}
                    <div className="flex gap-6">
                        {/* Needs Approval group */}
                        <div>
                            <div className="flex items-center mb-2">
                                <input
                                    type="checkbox"
                                    id="needs-approval"
                                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    checked={isGroupSelected("Needs Approval")}
                                    onChange={(e) => handleGroupSelection("Needs Approval", e.target.checked)}
                                    aria-label="Select all Needs Approval statuses"
                                />
                                <label
                                    htmlFor="needs-approval"
                                    className="ml-2 font-medium"
                                >
                                    Needs Approval
                                </label>
                                {isGroupPartiallySelected("Needs Approval") && (
                                    <div className="ml-2 h-1 w-1 rounded-full bg-blue-600"></div>
                                )}
                            </div>
                            <div className="space-y-1 pl-6">
                                {statusGroups["Needs Approval"].map(status => (
                                    <div key={status} className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id={`status-${status}`}
                                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                            checked={selectedStatuses.includes(status)}
                                            onChange={(e) => handleStatusSelection(status, e.target.checked)}
                                        />
                                        <label
                                            htmlFor={`status-${status}`}
                                            className="ml-2 text-sm text-gray-700"
                                        >
                                            {status}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Needs Appeal Decision group */}
                        <div>
                            <div className="flex items-center mb-2">
                                <input
                                    type="checkbox"
                                    id="needs-appeal"
                                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    checked={isGroupSelected("Needs Appeal Decision")}
                                    onChange={(e) => handleGroupSelection("Needs Appeal Decision", e.target.checked)}
                                    aria-label="Select all Needs Appeal Decision statuses"
                                />
                                <label
                                    htmlFor="needs-appeal"
                                    className="ml-2 font-medium"
                                >
                                    Needs Appeal Decision
                                </label>
                                {isGroupPartiallySelected("Needs Appeal Decision") && (
                                    <div className="ml-2 h-1 w-1 rounded-full bg-blue-600"></div>
                                )}
                            </div>
                            <div className="space-y-1 pl-6">
                                {statusGroups["Needs Appeal Decision"].map(status => (
                                    <div key={status} className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id={`status-${status}`}
                                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                            checked={selectedStatuses.includes(status)}
                                            onChange={(e) => handleStatusSelection(status, e.target.checked)}
                                        />
                                        <label
                                            htmlFor={`status-${status}`}
                                            className="ml-2 text-sm text-gray-700"
                                        >
                                            {status}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* MultiSelect Dropdown for other statuses */}
                    <div className="relative">
                        <button
                            type="button"
                            className="inline-flex justify-between px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            aria-haspopup="listbox"
                            aria-expanded={isDropdownOpen}
                        >
                            Additional Statuses
                            {isDropdownOpen ? (
                                <ChevronUp className="w-5 h-5 ml-2 -mr-1 text-gray-400" />
                            ) : (
                                <ChevronDown className="w-5 h-5 ml-2 -mr-1 text-gray-400" />
                            )}
                        </button>

                        {isDropdownOpen && (
                            <div className="absolute z-10 w-64 mt-1 bg-white rounded-md shadow-lg">
                                <ul className="py-1 overflow-auto text-base rounded-md max-h-60 focus:outline-none sm:text-sm">
                                    {/* Active group (renamed) */}
                                    <li className="px-3 py-2 font-medium text-gray-900 bg-gray-100">
                                        Active & In Progress
                                    </li>
                                    {statusGroups["Active"].map(status => (
                                        <li
                                            key={status}
                                            className="flex items-center px-3 py-2 cursor-pointer hover:bg-gray-100"
                                            onClick={() => handleStatusSelection(
                                                status,
                                                !selectedStatuses.includes(status)
                                            )}
                                        >
                                            <div className="flex items-center w-full">
                                                <div className="flex items-center h-5">
                                                    <input
                                                        type="checkbox"
                                                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                        checked={selectedStatuses.includes(status)}
                                                        onChange={() => { }} // Handled by parent onClick
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </div>
                                                <label className="ml-3 text-sm text-gray-700">
                                                    {status}
                                                </label>
                                            </div>
                                            {selectedStatuses.includes(status) && (
                                                <Check className="w-4 h-4 text-blue-600" />
                                            )}
                                        </li>
                                    ))}

                                    {/* Other group */}
                                    <li className="px-3 py-2 font-medium text-gray-900 bg-gray-100">
                                        Other
                                    </li>
                                    {statusGroups["Other"].map(status => (
                                        <li
                                            key={status}
                                            className="flex items-center px-3 py-2 cursor-pointer hover:bg-gray-100"
                                            onClick={() => handleStatusSelection(
                                                status,
                                                !selectedStatuses.includes(status)
                                            )}
                                        >
                                            <div className="flex items-center w-full">
                                                <div className="flex items-center h-5">
                                                    <input
                                                        type="checkbox"
                                                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                        checked={selectedStatuses.includes(status)}
                                                        onChange={() => { }} // Handled by parent onClick
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </div>
                                                <label className="ml-3 text-sm text-gray-700">
                                                    {status}
                                                </label>
                                            </div>
                                            {selectedStatuses.includes(status) && (
                                                <Check className="w-4 h-4 text-blue-600" />
                                            )}
                                        </li>
                                    ))}

                                    {/* Rejected group */}
                                    <li className="px-3 py-2 font-medium text-gray-900 bg-gray-100">
                                        Rejected
                                    </li>
                                    {statusGroups["Rejected"].map(status => (
                                        <li
                                            key={status}
                                            className="flex items-center px-3 py-2 cursor-pointer hover:bg-gray-100"
                                            onClick={() => handleStatusSelection(
                                                status,
                                                !selectedStatuses.includes(status)
                                            )}
                                        >
                                            <div className="flex items-center w-full">
                                                <div className="flex items-center h-5">
                                                    <input
                                                        type="checkbox"
                                                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                        checked={selectedStatuses.includes(status)}
                                                        onChange={() => { }} // Handled by parent onClick
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </div>
                                                <label className="ml-3 text-sm text-gray-700">
                                                    {status}
                                                </label>
                                            </div>
                                            {selectedStatuses.includes(status) && (
                                                <Check className="w-4 h-4 text-blue-600" />
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    <div className="relative border border-gray-300">
                        <p className="absolute top-0 left-0 right-0 z-10 flex items-center justify-center cursor-pointer h-full bg-white pointer-events-none">
                            Additional Statuses
                            <ChevronDown className="w-5 h-5 ml-2 -mr-1 text-gray-400" />
                        </p>

                        <Select
                            style={{ width: 200, cursor: 'pointer', height: 35 }}
                            placeholder="Additional Statuses"
                            mode="multiple"
                            maxTagCount={0}
                            maxTagPlaceholder={() => null}
                            tagRender={() => null}
                            options={[
                                {
                                    label: 'Active & In Progress',
                                    options: statusGroups["Active"].map(status => ({ label: status, value: status }))
                                },
                                {
                                    label: 'Other',
                                    options: statusGroups["Other"].map(status => ({ label: status, value: status }))
                                }, {
                                    label: 'Rejected',
                                    options: statusGroups["Rejected"].map(status => ({ label: status, value: status }))
                                },
                            ]}
                            onChange={(e, option) => {
                                console.log(e, option, 'e option')
                                handleStatusSelectionDropdown(e)
                            }}
                        />
                    </div>

                </div>

            </div>

            {/* Display active filters */}
            {selectedStatuses.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                    <div className="flex items-center">
                        <span className="text-sm text-gray-500">Active filters:</span>
                        <button
                            className="ml-2 text-sm text-blue-600 hover:text-blue-800"
                            onClick={clearAllFilters}
                        >
                            Clear all
                        </button>
                    </div>

                    {/* Show individual status tags for all selected statuses */}
                    {selectedStatuses.map(status => (
                        <div
                            key={status}
                            className="flex items-center px-2 py-1 text-sm bg-blue-100 text-blue-800 rounded"
                        >
                            <span>{status}</span>
                            <button
                                className="ml-1 text-blue-600 hover:text-blue-800"
                                onClick={() => removeFilter(status)}
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default StatusFilter;